import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PROJECT_STATUS, formatDate } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

const EMPTY = { name:"", description:"", area_ids:[], leader_id:"", start_date:"", end_date:"", budget:"", status:"planificacion" };

export default function ProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // null | {mode:"new"} | {mode:"edit", id}
  const [confirmDel, setConfirmDel] = useState(null); // project object
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [p, a, u] = await Promise.all([api.get("/projects"), api.get("/areas"), api.get("/users")]);
    setProjects(p.data); setAreas(a.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const canManage = user.role === "administrador" || user.role === "coordinador";

  const openNew = () => { setForm(EMPTY); setModal({ mode: "new" }); };
  const openEdit = (p) => {
    setForm({
      name: p.name || "",
      description: p.description || "",
      area_ids: p.area_ids || [],
      leader_id: p.leader_id || "",
      start_date: p.start_date || "",
      end_date: p.end_date || "",
      budget: p.budget != null ? String(p.budget) : "",
      status: p.status || "planificacion",
    });
    setModal({ mode: "edit", id: p.id });
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    const payload = { ...form, budget: form.budget ? Number(form.budget) : null, leader_id: form.leader_id||null, start_date: form.start_date||null, end_date: form.end_date||null };
    try {
      if (modal.mode === "new") {
        await api.post("/projects", payload);
        toast.success("Proyecto creado");
      } else {
        await api.put(`/projects/${modal.id}`, payload);
        toast.success("Proyecto actualizado");
      }
      setModal(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/projects/${confirmDel.id}`);
      toast.success("Proyecto eliminado");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al eliminar"); }
  };

  const userMap = Object.fromEntries(users.map(u => [u.id, u.name]));

  const semaforo = (p) => {
    if (p.status === "finalizado") return { color:"bg-emerald-500", label:"Finalizado" };
    if (!p.end_date) return { color:"bg-slate-300", label:"Sin fecha" };
    const days = Math.round((new Date(p.end_date) - new Date())/(1000*60*60*24));
    if (days < 0) return { color:"bg-crejpp", label:"Retrasado" };
    if (days < 7 && (p.computed_progress||0) < 80) return { color:"bg-amber-500", label:"En riesgo" };
    return { color:"bg-emerald-500", label:"En tiempo" };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Proyectos</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Gestión de proyectos</h1>
          <p className="text-sm text-slate-500 mt-1">Iniciativas institucionales, actividades y cronograma.</p>
        </div>
        {canManage && (
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-crejpp hover:bg-crejpp-dark text-white font-semibold rounded-md px-4 py-2 text-sm" data-testid="new-project-btn">
            <Plus className="w-4 h-4" /> Nuevo proyecto
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map(p => {
          const s = PROJECT_STATUS[p.status];
          const sem = semaforo(p);
          return (
            <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-5 relative group hover:shadow-sm transition-shadow" data-testid={`project-card-${p.id}`}>
              {canManage && (
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEdit(p); }}
                    className="p-1.5 rounded-md bg-white/90 border border-slate-200 text-slate-500 hover:text-crejpp hover:border-crejpp"
                    title="Editar proyecto"
                    data-testid={`edit-project-${p.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setConfirmDel(p); }}
                    className="p-1.5 rounded-md bg-white/90 border border-slate-200 text-slate-500 hover:text-crejpp hover:border-crejpp"
                    title="Eliminar proyecto"
                    data-testid={`delete-project-${p.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <Link to={`/proyectos/${p.id}`} className="block">
                <div className="flex items-start justify-between mb-3 pr-16">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${s.bg} ${s.text}`}>{s.label}</span>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${sem.color}`}></span>
                    <span className="text-[11px] text-slate-500">{sem.label}</span>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 line-clamp-2">{p.name}</h3>
                <p className="text-xs text-slate-500 line-clamp-2 mt-1">{p.description || "Sin descripción"}</p>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-slate-500">Avance</span>
                    <span className="font-semibold text-slate-900">{p.computed_progress || 0}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-crejpp" style={{ width: `${p.computed_progress || 0}%` }} />
                  </div>
                </div>
                <div className="flex justify-between items-center mt-4 pt-3 border-t border-slate-100 text-xs text-slate-500">
                  <div>
                    <div>Líder</div>
                    <div className="text-slate-800 font-medium">{userMap[p.leader_id] || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div>Fin</div>
                    <div className="text-slate-800 font-medium">{formatDate(p.end_date)}</div>
                  </div>
                </div>
              </Link>
            </div>
          );
        })}
        {projects.length === 0 && (
          <div className="col-span-full py-16 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg bg-white">
            No hay proyectos registrados.
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setModal(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg border border-slate-200 w-full max-w-lg p-6 max-h-[92vh] overflow-y-auto" data-testid={modal.mode === "new" ? "new-project-modal" : "edit-project-modal"}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{modal.mode === "new" ? "Nuevo proyecto" : "Editar proyecto"}</div>
                <h3 className="font-heading text-xl font-bold text-slate-900">{modal.mode === "new" ? "Crear proyecto" : form.name}</h3>
              </div>
              <button onClick={() => setModal(null)} className="p-2 hover:bg-slate-100 rounded-md"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Nombre"><input className="input" value={form.name} onChange={e => setForm({...form, name:e.target.value})} data-testid="project-name" /></Field>
              <Field label="Descripción"><textarea className="input" rows={3} value={form.description} onChange={e => setForm({...form, description:e.target.value})} /></Field>
              <Field label="Líder">
                <select className="input" value={form.leader_id} onChange={e => setForm({...form, leader_id:e.target.value})}>
                  <option value="">— Seleccione —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </Field>
              <Field label="Áreas involucradas">
                <div className="flex flex-wrap gap-2">
                  {areas.map(a => {
                    const on = form.area_ids.includes(a.id);
                    return (
                      <button key={a.id} type="button" onClick={() => setForm(f => ({...f, area_ids: on ? f.area_ids.filter(x => x !== a.id) : [...f.area_ids, a.id]}))} className={`text-xs px-3 py-1 rounded-full border ${on ? "bg-crejpp text-white border-crejpp" : "bg-white text-slate-700 border-slate-300 hover:border-crejpp"}`}>{a.name}</button>
                    );
                  })}
                </div>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Inicio"><input type="date" className="input" value={form.start_date} onChange={e => setForm({...form, start_date:e.target.value})} data-testid="project-start" /></Field>
                <Field label="Fin estimado"><input type="date" className="input" value={form.end_date} onChange={e => setForm({...form, end_date:e.target.value})} data-testid="project-end" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Presupuesto (USD)"><input type="number" className="input" value={form.budget} onChange={e => setForm({...form, budget:e.target.value})} /></Field>
                <Field label="Estado">
                  <select className="input" value={form.status} onChange={e => setForm({...form, status:e.target.value})}>
                    {Object.entries(PROJECT_STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </Field>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setModal(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm">Cancelar</button>
              <button onClick={save} className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark text-white rounded-md text-sm font-semibold" data-testid={modal.mode === "new" ? "create-project-btn" : "save-project-btn"}>
                {modal.mode === "new" ? "Crear" : "Guardar cambios"}
              </button>
            </div>
          </div>
          <style>{`.input { width:100%; padding:0.5rem 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; background:white; } .input:focus { outline:none; border-color:#EE1C25; box-shadow:0 0 0 3px rgba(238,28,37,0.12); }`}</style>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirmDel(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-lg border border-slate-200 w-full max-w-md p-6" data-testid="delete-project-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-red-50 text-crejpp flex items-center justify-center shrink-0"><AlertTriangle className="w-5 h-5" /></div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Eliminar proyecto</h3>
                <p className="text-sm text-slate-500 mt-1">Se eliminará <b className="text-slate-900">{confirmDel.name}</b> junto con sus actividades, responsabilidades e hitos. Esta acción no se puede deshacer.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm" data-testid="confirm-delete-cancel">Cancelar</button>
              <button onClick={doDelete} className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark text-white rounded-md text-sm font-semibold inline-flex items-center gap-1.5" data-testid="confirm-delete-btn">
                <Trash2 className="w-4 h-4" /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
